import { Component, OnInit, OnDestroy, ApplicationRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth } from '@angular/fire/auth';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { ConnectionService } from './services/connection.service';
import { BehaviorSubject, interval } from 'rxjs';
import { filter, first, skip } from 'rxjs/operators';
import { LayoutService } from './services/layout.service';
import { ToastService } from './services/toast.service';
import { WindowScrollService } from './services/window-scroll.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  title = 'MPACT Assessment';
  hideHeader = false;
  hideFooter = false;
  topNavNoBg = false;
  previousUrl: string = null;
  currentUrl: string = null;

  constructor(
    public layout: LayoutService,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private scroll: WindowScrollService,
    private connectionService: ConnectionService,
    private update: SwUpdate,
    private appRef: ApplicationRef,
    private auth: Auth,
  ) {
    this.updateClient();
    this.checkUpdate();

  }

  updateClient() {
    if (!this.update.isEnabled) return;

    this.update.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.update.activateUpdate().then(() => location.reload());
      }
    });
  }

  checkUpdate() {
    this.appRef.isStable.pipe(first(isStable => isStable)).subscribe(() => {
      interval(8 * 60 * 60 * 1000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.update.checkForUpdate();
      });
    });
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        if (event instanceof NavigationEnd) {
          this.hideHeader =
            this.route.firstChild.snapshot.data.hideHeader == true
              ? true
              : false;
          this.hideFooter =
            this.route.firstChild.snapshot.data.hideFooter == true
              ? true
              : false;
          this.topNavNoBg =
            this.route.firstChild.snapshot.data.topNavNoBg == true
              ? true
              : false;

          this.previousUrl = this.currentUrl;
          this.currentUrl = event.url;

          this.scroll.enableScroll();

          if (!this.previousUrl || !this.currentUrl) {
            return;
          }
          if (
            this.currentUrl.includes('/dash') &&
            (!this.previousUrl.includes('/dash') ||
              this.previousUrl.includes('/login'))
          ) {
            window.scrollTo(0, 0);
          } else if (this.hideFooter && this.hideHeader) {
            return;
          } else {
            window.scrollTo(0, 0);
          }
        }
      });

    // Skip the first emission to avoid showing "back online" on initial load
    this.connectionService.monitor().pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe((isConnected) => {
      // isConnected;
      if (isConnected) {
        // this.status = "ONLINE";
        this.toastService.showSuccess('You are back online.');
      } else {
        // this.status = "OFFLINE";
        this.toastService.showDanger(
          'You are now offline. Some parts of the app may not work properly until you are back online.',
        );
      }
    });
  }

  ngOnDestroy() {
    // Cleanup handled by takeUntilDestroyed
  }

  get showLoadingOverlay(): BehaviorSubject<boolean> {
    return this.layout.showLoadingOverlay;
  }

  get loadingOverlayMessage(): BehaviorSubject<string> {
    return this.layout.loadingOverlayMessage;
  }
}
